import { useEffect, useRef, useState } from "react";
import {
  Check,
  CheckCheck,
  Clock,
  AlertCircle,
  Play,
  Pause,
  Download,
  FileText,
  Image as ImageIcon,
  Music,
  Video as VideoIcon,
  Reply,
} from "lucide-react";
import { cn } from "@/lib/cn";
import type { WidgetMessage } from "@/types";
import { useSession } from "@/store/session-store";
import { ImageLightbox } from "./image-lightbox";

export function Messages() {
  const messages = useSession((s) => s.messages);
  const agentTyping = useSession((s) => s.agentTyping);
  const greeting = useSession((s) => s.branding?.greeting);
  const avatarUrl = useSession((s) => s.branding?.avatarUrl);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [lightboxSrc, setLightboxSrc] = useState<{
    url: string;
    name?: string | null;
  } | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, agentTyping]);

  return (
    <>
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-3 space-y-3 cz-scrollbar"
      >
        {messages.length === 0 && greeting && (
          <AgentBubble avatarUrl={avatarUrl}>
            <p className="text-sm leading-snug">{greeting}</p>
          </AgentBubble>
        )}

        {messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            message={msg}
            avatarUrl={avatarUrl}
            onImageClick={(url, name) => setLightboxSrc({ url, name })}
          />
        ))}

        {agentTyping && (
          <AgentBubble avatarUrl={avatarUrl}>
            <TypingDots />
          </AgentBubble>
        )}
      </div>

      {lightboxSrc && (
        <ImageLightbox
          src={lightboxSrc.url}
          fileName={lightboxSrc.name}
          onClose={() => setLightboxSrc(null)}
        />
      )}
    </>
  );
}

function MessageBubble({
  message,
  avatarUrl,
  onImageClick,
}: {
  message: WidgetMessage;
  avatarUrl?: string | null;
  onImageClick: (url: string, name?: string | null) => void;
}) {
  // Conventions: messages are stored from the dashboard's POV.
  //   direction === "outbound" → from the AGENT → renders LEFT (agent bubble)
  //   direction === "inbound"  → from the VISITOR (us) → renders RIGHT (own)
  const isAgent = message.direction === "outbound";
  const isOwn = message.direction === "inbound";
  const setReplyingTo = useSession((s) => s.setReplyingTo);

  if (isAgent) {
    return (
      <div className="group/bubble">
        <AgentBubble avatarUrl={avatarUrl}>
          <ReplyContext message={message} />
          <MessageBody
            message={message}
            isOwn={false}
            onImageClick={onImageClick}
          />
          <BubbleFooter message={message} isOwn={false} />
        </AgentBubble>
        <div className="pl-9 mt-0.5 opacity-0 group-hover/bubble:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={() => setReplyingTo(message)}
            className="text-[10px] text-cz-fg-muted hover:text-cz-fg inline-flex items-center gap-1"
          >
            <Reply className="h-3 w-3" /> Responder
          </button>
        </div>
      </div>
    );
  }

  if (isOwn) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] rounded-2xl rounded-br-md bg-cz-primary text-cz-on-primary px-3 py-2 shadow-sm">
          <ReplyContext message={message} />
          <MessageBody message={message} isOwn onImageClick={onImageClick} />
          <BubbleFooter message={message} isOwn />
        </div>
      </div>
    );
  }

  return null;
}

function AgentBubble({
  children,
  avatarUrl,
}: {
  children: React.ReactNode;
  avatarUrl?: string | null;
}) {
  return (
    <div className="flex gap-2 items-end">
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt=""
          className="h-7 w-7 rounded-full object-cover flex-shrink-0"
        />
      ) : (
        <div
          className="h-7 w-7 rounded-full bg-cz-primary text-cz-on-primary flex items-center justify-center text-[10px] font-bold flex-shrink-0"
          aria-hidden
        >
          IA
        </div>
      )}
      <div className="max-w-[80%] rounded-2xl rounded-bl-md bg-cz-surface-muted text-cz-fg px-3 py-2 shadow-sm">
        {children}
      </div>
    </div>
  );
}

function MessageBody({
  message,
  isOwn,
  onImageClick,
}: {
  message: WidgetMessage;
  isOwn: boolean;
  onImageClick: (url: string, name?: string | null) => void;
}) {
  if (message.type === "image" && message.mediaUrl) {
    return (
      <ImageBubble
        message={message}
        onClick={() => onImageClick(message.mediaUrl!, message.fileName)}
      />
    );
  }
  if (message.type === "audio" && message.mediaUrl) {
    return <AudioBubble message={message} isOwn={isOwn} />;
  }
  if (message.type === "video" && message.mediaUrl) {
    return <VideoBubble message={message} />;
  }
  if (message.type === "document" && message.mediaUrl) {
    return <DocumentBubble message={message} isOwn={isOwn} />;
  }
  if (message.type === "sticker" && message.mediaUrl) {
    return (
      <img
        src={message.mediaUrl}
        alt="Sticker"
        className="h-32 w-32 object-contain"
      />
    );
  }
  if (message.content) {
    return (
      <p className="text-sm leading-snug whitespace-pre-wrap break-words">
        {message.content}
      </p>
    );
  }
  return <p className="text-sm italic opacity-70">(mensagem)</p>;
}

// ── Image ────────────────────────────────────────────────────────────

function ImageBubble({
  message,
  onClick,
}: {
  message: WidgetMessage;
  onClick: () => void;
}) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  if (errored) {
    return <MediaUnavailable label="Imagem indisponível" />;
  }

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={onClick}
        className="block relative overflow-hidden rounded-lg group"
        aria-label="Ampliar imagem"
      >
        {!loaded && (
          <div className="absolute inset-0 bg-black/10 animate-pulse min-h-[120px] min-w-[160px]" />
        )}
        <img
          src={message.mediaUrl ?? ""}
          alt={message.fileName ?? "Imagem"}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          className={cn(
            "max-w-full max-h-64 object-contain transition-opacity",
            "group-hover:opacity-90",
            loaded ? "opacity-100" : "opacity-0",
          )}
        />
      </button>
      {message.content && (
        <p className="text-sm leading-snug whitespace-pre-wrap break-words">
          {message.content}
        </p>
      )}
    </div>
  );
}

// ── Audio ────────────────────────────────────────────────────────────

function AudioBubble({
  message,
  isOwn,
}: {
  message: WidgetMessage;
  isOwn: boolean;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const initialDuration =
    typeof message.metadata?.duration === "number"
      ? (message.metadata.duration as number)
      : 0;
  const [duration, setDuration] = useState(initialDuration);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onMeta = () => {
      if (Number.isFinite(a.duration)) setDuration(a.duration);
    };
    const onTime = () => {
      setCurrentTime(a.currentTime);
      if (a.duration > 0) setProgress(a.currentTime / a.duration);
    };
    const onEnd = () => {
      setPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    };
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
    } else {
      void a.play().catch(() => setPlaying(false));
    }
    setPlaying(!playing);
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const a = audioRef.current;
    if (!a || !Number.isFinite(a.duration) || a.duration === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(
      0,
      Math.min(1, (e.clientX - rect.left) / rect.width),
    );
    a.currentTime = ratio * a.duration;
    setProgress(ratio);
    setCurrentTime(a.currentTime);
  };

  const Icon = playing ? Pause : Play;
  const baseColor = isOwn
    ? "bg-white/20 hover:bg-white/30 text-cz-on-primary"
    : "bg-cz-primary/15 hover:bg-cz-primary/25 text-cz-primary";
  const trackColor = isOwn ? "bg-white/30" : "bg-cz-border";
  const trackFill = isOwn ? "bg-white/80" : "bg-cz-primary/70";

  return (
    <div className="flex items-center gap-3 min-w-[200px] max-w-[260px]">
      <audio ref={audioRef} src={message.mediaUrl ?? ""} preload="metadata" />
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? "Pausar" : "Reproduzir"}
        className={cn(
          "h-9 w-9 shrink-0 rounded-full flex items-center justify-center transition-colors",
          baseColor,
        )}
      >
        <Icon className="h-4 w-4" />
      </button>
      <div className="flex-1 min-w-0">
        <div
          className={cn("h-1 rounded-full cursor-pointer", trackColor)}
          onClick={handleSeek}
          role="slider"
          aria-label="Posição do áudio"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
        >
          <div
            className={cn("h-full rounded-full transition-all", trackFill)}
            style={{ width: `${progress * 100}%` }}
          />
        </div>
        <span className="text-[10px] mt-1 block opacity-70 tabular-nums">
          {playing || currentTime > 0
            ? formatDuration(currentTime)
            : duration > 0
              ? formatDuration(duration)
              : "0:00"}
        </span>
      </div>
    </div>
  );
}

// ── Video ────────────────────────────────────────────────────────────

function VideoBubble({ message }: { message: WidgetMessage }) {
  const poster =
    typeof message.metadata?.thumbnailUrl === "string"
      ? (message.metadata.thumbnailUrl as string)
      : undefined;

  return (
    <div className="space-y-1">
      <video
        src={message.mediaUrl ?? ""}
        poster={poster}
        controls
        playsInline
        preload="metadata"
        className="max-w-full max-h-64 rounded-lg bg-black"
      />
      {message.content && (
        <p className="text-sm leading-snug whitespace-pre-wrap break-words">
          {message.content}
        </p>
      )}
    </div>
  );
}

// ── Document ─────────────────────────────────────────────────────────

function DocumentBubble({
  message,
  isOwn,
}: {
  message: WidgetMessage;
  isOwn: boolean;
}) {
  const Icon = pickFileIcon(message.mimeType, message.fileName);
  const ext = extractExtension(message.mimeType, message.fileName);
  const sizeLabel = message.fileSize ? formatBytes(message.fileSize) : null;

  return (
    <div className="flex items-center gap-3 min-w-[200px] max-w-[280px]">
      <div
        className={cn(
          "h-10 w-10 rounded-xl flex items-center justify-center shrink-0",
          isOwn ? "bg-white/20" : "bg-cz-primary/10",
        )}
      >
        <Icon
          className={cn("h-5 w-5", isOwn ? "text-cz-on-primary" : "text-cz-primary")}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">
          {message.fileName ?? "Documento"}
        </p>
        <p className="text-[10px] opacity-70 uppercase">
          {[ext, sizeLabel].filter(Boolean).join(" · ") || "Arquivo"}
        </p>
      </div>
      <a
        href={message.mediaUrl ?? "#"}
        download={message.fileName ?? undefined}
        target="_blank"
        rel="noreferrer"
        aria-label="Baixar arquivo"
        className="opacity-70 hover:opacity-100 transition-opacity p-1"
      >
        <Download className="h-4 w-4" />
      </a>
    </div>
  );
}

// ── Reply context ────────────────────────────────────────────────────

function ReplyContext({ message }: { message: WidgetMessage }) {
  if (!message.replyTo) return null;
  const r = message.replyTo;
  const preview = r.content?.slice(0, 80) ?? labelForType(r.type);
  return (
    <div className="mb-1 px-2 py-1 rounded-md bg-black/10 border-l-2 border-current/40 text-[11px] leading-tight opacity-80">
      <div className="flex items-center gap-1 font-medium">
        <Reply className="h-3 w-3" />
        <span>Respondendo</span>
      </div>
      <p className="truncate mt-0.5">{preview}</p>
    </div>
  );
}

// ── Footer (time + status) ───────────────────────────────────────────

function BubbleFooter({
  message,
  isOwn,
}: {
  message: WidgetMessage;
  isOwn: boolean;
}) {
  const time = formatTime(message.timestamp);
  if (!time && !isOwn) return null;
  return (
    <div
      className={cn(
        "flex items-center justify-end gap-1 mt-1 text-[10px]",
        isOwn ? "opacity-70" : "opacity-50",
      )}
    >
      {time && <span>{time}</span>}
      {isOwn && <StatusIcon status={message.status} />}
    </div>
  );
}

function StatusIcon({ status }: { status: WidgetMessage["status"] }) {
  switch (status) {
    case "pending":
      return <Clock className="h-3 w-3" />;
    case "sent":
      return <Check className="h-3 w-3" />;
    case "delivered":
      return <CheckCheck className="h-3 w-3" />;
    case "read":
      return <CheckCheck className="h-3 w-3 text-blue-300" />;
    case "failed":
      return <AlertCircle className="h-3 w-3 text-red-300" />;
    default:
      return null;
  }
}

function TypingDots() {
  return (
    <div className="flex gap-1 py-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={cn(
            "h-1.5 w-1.5 rounded-full bg-cz-fg-muted",
            "inline-block",
          )}
          style={{
            animation: `cz-bounce-dot 1.2s infinite ${i * 0.15}s ease-in-out`,
          }}
        />
      ))}
    </div>
  );
}

function MediaUnavailable({ label }: { label: string }) {
  return (
    <div className="h-24 w-40 rounded-lg bg-black/10 flex items-center justify-center">
      <span className="text-xs opacity-70">{label}</span>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function formatTime(iso: string): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return "";
  const k = 1024;
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
  const v = bytes / Math.pow(k, i);
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

function extractExtension(
  mime?: string | null,
  fileName?: string | null,
): string | null {
  if (fileName && fileName.includes(".")) {
    const ext = fileName.split(".").pop();
    if (ext) return ext.toUpperCase();
  }
  if (mime && mime.includes("/")) return mime.split("/").pop()?.toUpperCase() ?? null;
  return null;
}

function pickFileIcon(
  mime?: string | null,
  fileName?: string | null,
): typeof FileText {
  const m = (mime ?? "").toLowerCase();
  const n = (fileName ?? "").toLowerCase();
  if (m.startsWith("image/") || /\.(png|jpg|jpeg|gif|webp|svg)$/.test(n))
    return ImageIcon;
  if (m.startsWith("audio/") || /\.(mp3|ogg|wav|m4a|opus)$/.test(n)) return Music;
  if (m.startsWith("video/") || /\.(mp4|webm|mov|avi)$/.test(n)) return VideoIcon;
  return FileText;
}

function labelForType(type: WidgetMessage["type"]): string {
  switch (type) {
    case "image":
      return "📷 Imagem";
    case "audio":
      return "🎤 Áudio";
    case "video":
      return "🎬 Vídeo";
    case "document":
      return "📎 Arquivo";
    default:
      return "Mensagem";
  }
}
